import React, { useEffect, useMemo, useState } from 'react';
import {
  MultiOrgOption,
  useMultiOrgHierarchy,
} from '../../hooks/useMultiOrgHierarchy';
import { useMultiOrgCookies } from '../../hooks/useMultiOrgCookies';
import { useTranslation } from 'react-i18next';
import { OrgTree } from '../../MultiOrg/MultiOrgTree';
import { getAccessibleOrgs } from './utils';
import { WorkingOrgModal } from '../../MultiOrg/WorkingOrgModal';
import ReactDOM from 'react-dom';
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  TextField,
} from '@mui/material';
import { Icon } from '@platform-ui/design-system';
import { Theme } from '../../theme';

function hasInvalidOrg(
  accessibleOrgs: MultiOrgOption[],
  currentOrgIds: string[],
) {
  return currentOrgIds.some(
    (id) => !accessibleOrgs.find((org) => org.organizationId === id),
  );
}

const MultiOrgPickerComponent = ({ reloadPage, menuElement, menuType }) => {
  const { data: hierarchy } = useMultiOrgHierarchy();
  const [contentContainer, setContentContainer] =
    useState<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const { workingOrg, selectedOrgIds, setSelectedOrgIds } =
    useMultiOrgCookies();

  const [searchTerm, setSearchTerm] = useState('');
  const [showSettings, setShowSettings] = useState(false);

  const accessibleOrgs = useMemo(
    () => getAccessibleOrgs(hierarchy),
    [hierarchy],
  );

  useEffect(() => {
    if (
      accessibleOrgs &&
      (selectedOrgIds.length === 0 ||
        hasInvalidOrg(accessibleOrgs, selectedOrgIds))
    ) {
      setSelectedOrgIds(accessibleOrgs.map((org) => org.organizationId));
      reloadPage();
    }
  }, [accessibleOrgs, selectedOrgIds]);

  const buttonLabel = useMemo(() => {
    return selectedOrgIds.length > 0 && accessibleOrgs
      ? accessibleOrgs
          .filter((org) => selectedOrgIds.includes(org.organizationId))
          .map((org) => org.organizationName)
          .join(', ')
      : t('MultiOrg.SelectOrganization');
  }, [selectedOrgIds, accessibleOrgs, t]);

  useEffect(() => {
    // Create a container for our React content
    const container = document.createElement('div');
    container.slot = 'options';
    container.className = 'multi-org-content-container';

    menuElement.appendChild(container);

    setContentContainer(container);

    return () => {
      if (container && container.parentNode) {
        container.parentNode.removeChild(container);
      }
    };
  }, [menuElement]);

  useEffect(() => {
    if (!menuElement) return;

    // if (menuElement.hasAttribute('label')) {
    //   (menuElement as any).setAttribute('label', buttonLabel);
    // } else if ('label' in menuElement) {
    //   (menuElement as any).label = buttonLabel;
    // }

    const targetElement = menuType === 'org' ? menuElement : menuElement;

    if (targetElement) {
      targetElement.setAttribute('label', buttonLabel);
    }
  }, [menuElement, buttonLabel]);

  // Only create the portal if we have a container
  if (!contentContainer) {
    return null;
  }

  const handleOrgChange = (value: string[]) => {
    setSelectedOrgIds(value);
  };

  if (!hierarchy?.userOrgIds?.length) {
    return null;
  }

  return (
    <>
      {ReactDOM.createPortal(
        <div className="multi-org-react-content">
          <Theme>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                maxHeight: '60vh',
                overflow: 'hidden',
              }}
            >
              <Box sx={{ height: '36px' }}>
                <TextField
                  fullWidth
                  variant="standard"
                  placeholder={t('MultiOrg.SearchOrganizations')}
                  value={searchTerm}
                  onChange={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setSearchTerm(e.target.value);
                  }}
                  InputProps={{
                    inputProps: {
                      'data-e2e': 'nav-org-input',
                    },
                    startAdornment: (
                      <InputAdornment position="start">
                        <Icon body="search" />
                      </InputAdornment>
                    ),
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => {
                            setShowSettings(true);
                          }}
                        >
                          <Icon body="settings" />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Divider
                sx={{ margin: '0 0 8px 0', color: 'rgba(0, 0, 0, 0.12)' }}
              />
              <Box
                sx={{
                  flexGrow: 1,
                  flexShrink: 1,
                  minHeight: 0,
                  overflowY: 'auto',
                }}
              >
                <OrgTree
                  organizations={hierarchy.options}
                  userOrgIds={hierarchy.userOrgIds}
                  searchTerm={searchTerm}
                  selectedOrgIds={selectedOrgIds}
                  onOrgChange={handleOrgChange}
                  workingOrgId={workingOrg}
                />
              </Box>
            </Box>
          </Theme>
        </div>,
        contentContainer,
      )}
      {showSettings && (
        <WorkingOrgModal
          open={showSettings}
          onClose={() => setShowSettings(false)}
          organizations={hierarchy.options}
          userOrgIds={hierarchy.userOrgIds}
          reloadPage={reloadPage}
        />
      )}
    </>
  );
};

export default MultiOrgPickerComponent;
